import { fromJS } from 'immutable';
import { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Raven from 'raven-js';
import getIn from 'transmute/getIn';
import getLangLocale from 'I18n/utils/getLangLocale';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import intersection from 'hs-lodash/intersection';
import { useStripe, useElements } from 'react-stripe-js';
import { useDataFetchingClient, useMutation } from 'data-fetching-client';
import { PENDING, SOURCE_ID_PAID, SUBMITTED } from '../constants/Statuses';
import { GET_CHECKOUT_SESSION, CREATE_PAYMENT_INTENT } from '../data/queries';
import { getIsIframeableUrl, getIsIframePreviewUrl, redirectToUrlAsync, appendEmailToUrlIfEnabled } from '../utils/urls';
import { usePaymentFormContext } from '../context/PaymentFormContext';
import { trackSuccessfulPaymentSubmission } from '../utils/tracking';
import { useIframeableContext } from '../context/IframeableContext';
import { EMBED_MESSAGE_TYPES } from '../constants/Embed';
import { sanitizeFormFieldValue } from '../utils/formFieldData';
import { getBuyerOverridesLineItems, getBuyerOverridesPartialPaymentAmount } from '../state/BuyerOverrides/selectors';
import { getStripeErrorMessageFromErrorCodeForCard, getStripeErrorMessageFromErrorCodeForACH } from '../utils/stripe';
import BuyerOverridesActionTypes from '../state/BuyerOverrides/actionTypes';
import { fetchBuyerOverridesSubtotals } from '../data/BuyerOverridesApi';
import updateCheckoutSessionSubtotals from '../utils/updateCheckoutSessionSubtotals';
import { sendPostMessage } from '../utils/sendPostMessage';
import { ACH, BACS, CARD, PADS, SEPA } from '../constants/PaymentMethods';
import { isACHorPADS, isACHorSEPA } from '../utils/paymentMethods';
import { getIntervalDescription } from '../utils/intervalDescription';
import { DISCOUNT_CODE_NOT_VALID } from '../constants/ValidationErrors';
import { getTaxCalculationId } from '../state/calculateTaxSlice';
import { STRIPE_CONFIRMATION_TOKEN_GATE } from '../constants/gates';
import { jsx as _jsx } from "react/jsx-runtime";
const getStripeConfirmationTokenId = async ({
  stripe,
  elements,
  billingDetails
}) => {
  var _createConfirmationTo;
  const createConfirmationTokenResult = await stripe
  // @ts-expect-error  this is a stripe js function but we may need to upgrade to a newer version
  .createConfirmationToken({
    elements,
    params: {
      payment_method_data: {
        billing_details: billingDetails
      }
    }
  });
  if (createConfirmationTokenResult.error) {
    throw new Error(`stripe.createConfirmationToken result error: ${createConfirmationTokenResult.error.message}`);
  }
  const confirmationTokenId = createConfirmationTokenResult === null || createConfirmationTokenResult === void 0 || (_createConfirmationTo = createConfirmationTokenResult.confirmationToken) === null || _createConfirmationTo === void 0 ? void 0 : _createConfirmationTo.id;
  if (!confirmationTokenId) {
    throw new Error(`stripe.createConfirmationToken result missing confirmation token id`);
  }
  return {
    confirmationTokenId,
    paymentMethodId: undefined
  };
};
const getStripePaymentMethodId = async ({
  stripe,
  elements,
  billingDetails
}) => {
  var _createPaymentMethodR;
  const createPaymentMethodResult = await stripe.createPaymentMethod({
    elements,
    params: {
      billing_details: billingDetails
    }
  });
  if (createPaymentMethodResult.error) {
    throw new Error(`stripe.createPaymentMethod result error: ${createPaymentMethodResult.error.message}`);
  }
  if (!((_createPaymentMethodR = createPaymentMethodResult.paymentMethod) !== null && _createPaymentMethodR !== void 0 && _createPaymentMethodR.id)) {
    throw new Error(`stripe.createPaymentMethod result missing payment method id`);
  }
  return {
    confirmationTokenId: undefined,
    paymentMethodId: createPaymentMethodResult.paymentMethod.id
  };
};
/*
  A hook that returns a function that when called, creates a Stripe PaymentIntent
  and subsequently confirms the PaymentIntent using data from Stripe's credit
  card inputs (eg. CardNumberElement) or custom ACH inputs.
*/
export const useSubmitPayment = () => {
  const {
    pathname
  } = useLocation();
  const {
    formState,
    dispatch
  } = usePaymentFormContext();
  const reduxDispatch = useDispatch();
  const {
    iframeableContext
  } = useIframeableContext();
  const isIframeableUrl = getIsIframeableUrl(pathname);
  const buyerOverridesLineItems = useSelector(getBuyerOverridesLineItems);
  const partialPaymentAmount = useSelector(getBuyerOverridesPartialPaymentAmount);
  const isRouteIframePreview = getIsIframePreviewUrl(pathname);
  const taxCalculationId = useSelector(getTaxCalculationId);
  const setIsCheckoutSubmitPending = useCallback(isSubmitPending => {
    dispatch({
      type: 'setIsCheckoutSubmitPending',
      data: {
        value: isSubmitPending
      }
    });
  }, [dispatch]);
  const [confirmPaymentErrorMessage, setConfirmPaymentErrorMessage] = useState('');
  const [confirmIntentError, setConfirmIntentError] = useState(false);
  const [isRecaptchaRetry, setRecaptchaRetry] = useState(false);
  const resetError = useCallback(() => {
    setRecaptchaRetry(false);
    setConfirmIntentError(false);
    setConfirmPaymentErrorMessage('');
    FloatingAlertStore.removeAlert('validation-error');
  }, []);
  const client = useDataFetchingClient();
  const stripe = useStripe();
  const elements = useElements();
  const [createPaymentIntent, {
    loading,
    error
  }] = useMutation(CREATE_PAYMENT_INTENT, {
    onError: err => {
      if (isIframeableUrl && !!iframeableContext) {
        iframeableContext.sendMessage(EMBED_MESSAGE_TYPES.FAILED);
      }
      const errorSubcategory = getIn(['networkError', 'responseJSON', 'subCategory'], err);
      const getCheckoutSessionData = client.readQuery({
        query: GET_CHECKOUT_SESSION
      });
      const checkoutSession = getCheckoutSessionData && getCheckoutSessionData.checkoutSession;
      switch (errorSubcategory) {
        case 'PayRequestError.IDEMPOTENT_PAYMENT_IN_PROGRESS':
          client.writeQuery({
            query: GET_CHECKOUT_SESSION,
            data: {
              checkoutSession: Object.assign({}, checkoutSession, {
                status: SOURCE_ID_PAID
              })
            }
          });
          break;
        case 'PayRequestError.SESSION_NOT_PAYABLE':
          FloatingAlertStore.addAlert({
            titleText: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "payments.errorAlert.payment.sessionNotPayableTitle"
            }),
            message: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "payments.errorAlert.payment.sessionNotPayableText"
            }),
            type: 'danger'
          });
          break;
        case 'RecaptchaVerificationError.INVALID_TOKEN_RETRY':
          {
            setRecaptchaRetry(true);
            setConfirmPaymentErrorMessage( /*#__PURE__*/_jsx(FormattedMessage, {
              message: "payments.errorAlert.payment.recaptchaFailed"
            }));
            break;
          }
        case 'RecaptchaVerificationError.INVALID_TOKEN_NO_RETRY':
          {
            setConfirmPaymentErrorMessage( /*#__PURE__*/_jsx(FormattedMessage, {
              message: "payments.errorAlert.payment.recaptchaFailed"
            }));
            break;
          }
        case 'CheckoutRecaptchaVerificationError.RECAPTCHA_DENIED':
          {
            const companyName = getIn(['networkError', 'responseJSON', 'context', 'companyName'], err);
            setConfirmPaymentErrorMessage(companyName ? /*#__PURE__*/_jsx(FormattedMessage, {
              message: "payments.errorAlert.payment.recaptchaFailedWithCompany",
              options: {
                companyName
              }
            }) : /*#__PURE__*/_jsx(FormattedMessage, {
              message: "payments.errorAlert.payment.recaptchaFailed"
            }));
            break;
          }
        case 'CheckoutPayRequestError.SESSION_EXPIRED':
          FloatingAlertStore.addAlert({
            titleText: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "payments.errorAlert.payment.sessionExpiredTitle"
            }),
            message: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "payments.errorAlert.payment.sessionExpiredText"
            }),
            type: 'danger'
          });
          break;
        case DISCOUNT_CODE_NOT_VALID:
          {
            dispatch({
              type: 'setDiscountCodeError',
              data: true
            });
            setConfirmPaymentErrorMessage( /*#__PURE__*/_jsx(FormattedMessage, {
              message: "payments.errorAlert.payment.invalidDiscountCode"
            }));
            break;
          }
        case 'PaymentsValidationError.EXPECTED_TOTAL_WITH_FUTURE_START_DATE_LINE_ITEMS_DOES_NOT_MATCH_CALCULATED_TOTAL':
          {
            const latestBilledAtCheckoutLineItemsIds = getIn(['networkError', 'responseJSON', 'context', 'billedAtCheckoutLineItemIds'], err
            //BE has to send a string array but lineItem.id is a number so this convert it to a Array<number>
            ).map(Number);
            const futureStartDateLineItems = fromJS(checkoutSession).get('lineItems').toJS().filter(li => li.billedAtCheckout === false);
            const futureLineItemIds = futureStartDateLineItems.map(l => l.id);
            const expiredLineItemIds = intersection(latestBilledAtCheckoutLineItemsIds, futureLineItemIds);
            reduxDispatch({
              type: BuyerOverridesActionTypes.SET_EXPIRED_FUTURE_START_DATE_LINE_ITEMS,
              data: {
                expiredFutureStartDateLineItemIds: expiredLineItemIds
              }
            });
            break;
          }
        default:
          {
            Raven.captureMessage('createPaymentIntent did not succeed', {
              extra: errorSubcategory
            });
            FloatingAlertStore.addAlert({
              titleText: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "payments.errorAlert.payment.title"
              }),
              message: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "payments.errorAlert.payment.text"
              }),
              type: 'danger'
            });
          }
      }
    }
  });
  const submit = useCallback(args => {
    var _formattedJSON$billin, _formattedJSON$billin2, _checkoutSession$gate;
    const walletType = args && args.walletType || null;
    const feeType = args && args.feeType || 'NONE';
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet.
      return null;
    }
    setIsCheckoutSubmitPending(true);
    resetError();
    const getCheckoutSessionData = client.readQuery({
      query: GET_CHECKOUT_SESSION
    });
    const checkoutSession = getCheckoutSessionData && getCheckoutSessionData.checkoutSession;
    const immutableSession = fromJS(checkoutSession);
    const isPartialPaymentsEnabled = immutableSession.getIn(['partialPaymentsInfo', 'isPartialPaymentsEnabled']);
    const baseSuccessUrl = immutableSession.get('successUrl');
    const sourceId = immutableSession.get('sourceId');
    const successUrl = baseSuccessUrl ? appendEmailToUrlIfEnabled(baseSuccessUrl, formState.get('email'), !!(checkoutSession !== null && checkoutSession !== void 0 && checkoutSession.includeEmailInSuccessRedirect)) : null;
    // NOTE: checkoutSession.total is the value that we display to buyers and
    // corresponds to the first payment total, that is, the payment expected
    // upon submission. This value can be updated if the buyer updates a
    // lineItem's quantity or price (in js/utils/updateCheckoutSessionSubtotals).
    // We pass this into the intent endpoint so that the BE can
    // sanity check that this value matches the expected value as calculated
    // from the lineItems. This should always be the case, so this check is
    // a last-ditch sanity check to make sure we don't charge buyers
    // something different than what we saw. Since we don't expect to see this,
    // we let the BE alert Sentry and display a generic error message here.
    const expectedFirstPaymentTotal = immutableSession.get('total');
    const isValidZeroCheckoutSession = expectedFirstPaymentTotal === 0;
    const formattedJSON = formState.toJS();
    const {
      discountCode,
      isDiscountCodeApplied
    } = formattedJSON;
    const buyerOverrides = () => {
      if (isPartialPaymentsEnabled === true) {
        return {
          buyerOverrides: {
            paymentAmount: partialPaymentAmount
          }
        };
      }
      if (buyerOverridesLineItems.length > 0) {
        if (isDiscountCodeApplied) {
          return {
            buyerOverrides: {
              lineItems: buyerOverridesLineItems,
              discountCode
            }
          };
        } else {
          return {
            buyerOverrides: {
              lineItems: buyerOverridesLineItems
            }
          };
        }
      } else {
        if (isDiscountCodeApplied) {
          return {
            buyerOverrides: {
              discountCode,
              lineItems: []
            }
          };
        }
      }
      return false;
    };
    const getFormFieldData = () => {
      const data = immutableSession.get('formFields').map((formField, i) => ({
        formFieldBrief: formField.toJS(),
        value: i === 0 ? formattedJSON.email : sanitizeFormFieldValue(formState.getIn(['customData', formField.get('name')]))
      })).toArray().filter(formField => formField.value !== null && formField.value !== undefined);
      return data;
    };
    const getDiscountData = () => {
      const discountCodeResponse = immutableSession.get('subtotalsPublicResponse') && immutableSession.get('subtotalsPublicResponse').get('discountCodeResponse');
      if (discountCodeResponse) {
        return discountCodeResponse.get('checkoutSessionDiscount').toJS();
      }
      return null;
    };
    const getIntervalDescriptionString = () => {
      const recurringSubtotals = immutableSession.getIn(['subtotalsPublicResponse', 'recurringSubtotals']);
      const hasRecurringSubtotals = recurringSubtotals && recurringSubtotals.size > 0;
      if (formattedJSON.paymentMethod === PADS && hasRecurringSubtotals) {
        return getIntervalDescription(immutableSession.get('currencyCode'), recurringSubtotals);
      }
      return null;
    };
    const paymentMethodOptions = formattedJSON.paymentMethod === PADS ? {
      pads: {
        intervalDescription: getIntervalDescriptionString(),
        transactionType: formattedJSON.bankInfo.transactionType
      }
    } : null;
    const isACH = formattedJSON.paymentMethod === ACH;
    const isBACS = formattedJSON.paymentMethod === BACS;
    const isPADS = formattedJSON.paymentMethod === PADS;
    const isSEPA = formattedJSON.paymentMethod === SEPA;
    const isBankTransfer = isACH || isBACS || isPADS || isSEPA;
    const shouldUseAuthModal = isACHorSEPA(formattedJSON.paymentMethod);
    if (isIframeableUrl && !!iframeableContext) {
      iframeableContext.sendMessage(EMBED_MESSAGE_TYPES.PROCESSING);
    }
    if (isRouteIframePreview) {
      setTimeout(() => {
        if (successUrl && !!iframeableContext) {
          iframeableContext.sendMessage(EMBED_MESSAGE_TYPES.SUCCESS_WITH_REDIRECT, {
            href: successUrl
          });
        } else {
          if (!checkoutSession || !checkoutSession.processorType || !checkoutSession.adjustments) {
            Raven.captureMessage(`submitPayment submit method IframePreview missing data`);
            return;
          }
          client.writeQuery({
            query: GET_CHECKOUT_SESSION,
            data: {
              checkoutSession: Object.assign({}, checkoutSession, {
                status: SUBMITTED,
                paymentDetails: {
                  paymentMethod: {
                    type: formattedJSON.paymentMethod
                  },
                  customer: {
                    email: formState.get('email')
                  }
                }
              })
            }
          });
          if (iframeableContext) {
            iframeableContext.sendMessage(EMBED_MESSAGE_TYPES.SUCCESS);
          }
          // IFramePreview does not call createPaymentIntent,
          // so this sets submitting state to false once this fake submit is done
          setIsCheckoutSubmitPending(false);
        }
      }, 2500);
      return null;
    }
    // stripe throws an error if postal code is undefined and requries an empty string, relevant for countries without a postal code such as UAE,
    // however stripe also throws an error if country is an empty string and requries undefined, relevant for payment methods such as ACH that don't collect country
    const billingAddress = immutableSession.get('collectFullBillingAddress') ? {
      city: formattedJSON.billingAddress.city,
      country: ((_formattedJSON$billin = formattedJSON.billingAddress.country) === null || _formattedJSON$billin === void 0 ? void 0 : _formattedJSON$billin.toUpperCase()) || undefined,
      line1: formattedJSON.billingAddress.address,
      line2: formattedJSON.billingAddress.address2,
      postal_code: formattedJSON.billingAddress.postalCode,
      state: formattedJSON.billingAddress.state
    } : {
      country: ((_formattedJSON$billin2 = formattedJSON.billingAddress.country) === null || _formattedJSON$billin2 === void 0 ? void 0 : _formattedJSON$billin2.toUpperCase()) || undefined,
      postal_code: formattedJSON.billingAddress.postalCode
    };
    const billingDetails = {
      address: billingAddress,
      email: formattedJSON.email,
      name: isACHorPADS(formattedJSON.paymentMethod) ? formattedJSON.bankInfo.accountHolderName : formattedJSON.billingAddress.fullName
    };
    const isUngatedToConfirmationTokens = checkoutSession === null || checkoutSession === void 0 || (_checkoutSession$gate = checkoutSession.gates) === null || _checkoutSession$gate === void 0 ? void 0 : _checkoutSession$gate.includes(STRIPE_CONFIRMATION_TOKEN_GATE);
    const shouldCreatePaymentMethod = [CARD, ACH, BACS, SEPA].includes(formattedJSON.paymentMethod);
    const stripePromise = (() => {
      if (isUngatedToConfirmationTokens) return getStripeConfirmationTokenId({
        stripe,
        elements,
        billingDetails
      });
      if (shouldCreatePaymentMethod) {
        return getStripePaymentMethodId({
          stripe,
          elements,
          billingDetails
        });
      }
      return Promise.resolve({
        confirmationTokenId: undefined,
        paymentMethodId: undefined
      });
    })();
    return stripePromise.then(({
      confirmationTokenId,
      paymentMethodId
    }) => {
      const formattedData = Object.assign({
        email: formattedJSON.email,
        shippingAddress: Object.assign({}, formattedJSON.shippingAddress, {
          country: formattedJSON.shippingAddress.country.toUpperCase()
        }),
        formFieldData: getFormFieldData(),
        appliedDiscountCodeMetadata: getDiscountData(),
        paymentMethod: formattedJSON.paymentMethod,
        availablePaymentMethods: args && args.availablePaymentMethods,
        paymentMethodOptions,
        buyerLocale: immutableSession.get('buyerLocale') || getLangLocale()
      }, buyerOverrides(), {
        expectedFirstPaymentTotal,
        expectedPaymentAmount: immutableSession.getIn(['subtotalsPublicResponse', 'paymentAmount']),
        recaptchaToken: args && args.recaptchaToken,
        taxCalculationId,
        confirmationTokenId,
        paymentMethodId
      });
      return createPaymentIntent({
        variables: {
          data: formattedData
        }
      });
    }).then(response => {
      if (!response || response.errors) {
        // Mutation failed
        if (response && response.errors) {
          return Promise.reject(response.errors);
        }
        return Promise.reject(new Error('Payment Element createPaymentIntent mutation response contained no response'));
      }
      const clientSecret = response && response.data && response.data.paymentIntentDetails && response.data.paymentIntentDetails.clientSecret;
      if (!clientSecret) {
        return Promise.reject(new Error('createPaymentIntent mutation response contained no clientSecret'));
      }
      const stripeConfirmMethod = isValidZeroCheckoutSession ? stripe.confirmSetup : stripe.confirmPayment;
      const metaDataObject = isACH ? {
        metadata: {
          account_holder_type: formattedJSON.bankInfo.accountHolderType.toLowerCase()
        }
      } : null;
      return stripeConfirmMethod({
        clientSecret,
        elements,
        redirect: 'if_required',
        // Not currently using redirect based methods: https://stripe.com/docs/payments/bank-redirects
        confirmParams: {
          payment_method_data: Object.assign({}, metaDataObject, {
            billing_details: {
              address: billingAddress,
              email: formattedJSON.email,
              name: isACHorPADS(formattedJSON.paymentMethod) ? formattedJSON.bankInfo.accountHolderName : formattedJSON.billingAddress.fullName
              // for SEPA we're using the Payment Element name field so that will automatically update here
            }
          })
        }
      });
    }).then(result => {
      if (result.error) {
        dispatch({
          type: 'setStripeError',
          data: result.error
        });
        if (isIframeableUrl && !!iframeableContext) {
          iframeableContext.sendMessage(EMBED_MESSAGE_TYPES.FAILED);
        }
        const translatedErrorMessage = isACH ? getStripeErrorMessageFromErrorCodeForACH(result.error) : getStripeErrorMessageFromErrorCodeForCard(result.error);
        if (translatedErrorMessage) {
          setConfirmPaymentErrorMessage(translatedErrorMessage);
        } else {
          setConfirmIntentError(true);
          if (isPADS && result.error.code === 'incomplete_payment_details' || isBACS && result.error.code === 'modify_bacs_debit_bank_details') {
            return null;
          }
          FloatingAlertStore.addAlert({
            id: 'validation-error',
            titleText: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "payments.errorAlert.default.title"
            }),
            message: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "payments.errorAlert.default.text"
            }),
            type: 'danger'
          });
          Raven.captureMessage(`stripe confirm intent failed with error: ${result.error.message}`);
        }
      } else {
        const intentStatus = isValidZeroCheckoutSession ? result.setupIntent.status : result.paymentIntent.status;
        if (/^(pending|processing|succeeded)$/.test(intentStatus) && !partialPaymentAmount) {
          // Ensure we only display valid post-purchase lineItems on success.
          // This is a hack to work around subtotals being implemented as a query instead of a function call.
          // It should change to a function call along with the subtotals v2 updates.
          // https://git.hubteam.com/HubSpot/Buyer-Exp-Issues/issues/673
          //
          // NOTE: When optional products are left nonselected, this call will overwrite the line items
          // with FEWER line items than session.lineItems originally had.  As long as items in the success
          // view are non-interactive, that won't matter.  If they ever become interactive, we'll need
          // a different architecture for storing line item blur/toggle/etc states.
          //
          // We skip this request with partial payments because lineItems are aggregated
          fetchBuyerOverridesSubtotals({
            buyerOverrides: {
              lineItems: buyerOverridesLineItems,
              discountCode: formattedJSON.discountCode || undefined
            },
            paymentSessionId: immutableSession.get('id'),
            taxCalculationId: taxCalculationId !== null && taxCalculationId !== void 0 ? taxCalculationId : undefined,
            paymentMethod: formattedJSON.paymentMethod
          }).then(data => {
            updateCheckoutSessionSubtotals(client, data);
          }).catch(() => {
            Raven.captureMessage('subtotals request failed post-purchase');
          });
        }
        if (!checkoutSession || !checkoutSession.processorType || !checkoutSession.adjustments) {
          Raven.captureMessage(`submitPayment createPaymentIntent stripeCardMethod .then  method missing data`);
          return;
        }
        if (intentStatus === 'pending' || intentStatus === 'processing') {
          if (isBankTransfer) {
            trackSuccessfulPaymentSubmission(formState, immutableSession, buyerOverridesLineItems, walletType, feeType);
            if (isIframeableUrl && !!iframeableContext) {
              iframeableContext.sendMessage(EMBED_MESSAGE_TYPES.SUCCESS, {
                data: expectedFirstPaymentTotal
              });
            }
            sendPostMessage(EMBED_MESSAGE_TYPES.SUCCESS, sourceId);
          }
          if (successUrl && !isIframeableUrl) {
            redirectToUrlAsync(successUrl);
            return;
          }
          if (!isIframeableUrl) {
            client.writeQuery({
              query: GET_CHECKOUT_SESSION,
              data: {
                checkoutSession: Object.assign({}, checkoutSession, {
                  status: PENDING,
                  paymentDetails: {
                    paymentMethod: {
                      type: formattedJSON.paymentMethod
                    },
                    customer: {
                      email: formState.get('email')
                    }
                  }
                })
              }
            });
          }
          return;
        }
        if (intentStatus === 'succeeded') {
          trackSuccessfulPaymentSubmission(formState, immutableSession, buyerOverridesLineItems, walletType, feeType);
          if (isIframeableUrl && !!iframeableContext) {
            iframeableContext.sendMessage(EMBED_MESSAGE_TYPES.SUCCESS, {
              data: expectedFirstPaymentTotal
            });
          }
          sendPostMessage(EMBED_MESSAGE_TYPES.SUCCESS, sourceId);
          if (successUrl && !isIframeableUrl) {
            redirectToUrlAsync(successUrl);
            return;
          }
          if (!isIframeableUrl) {
            client.writeQuery({
              query: GET_CHECKOUT_SESSION,
              data: {
                checkoutSession: Object.assign({}, checkoutSession, {
                  status: SUBMITTED,
                  paymentDetails: {
                    paymentMethod: {
                      type: formattedJSON.paymentMethod
                    },
                    customer: {
                      email: formState.get('email')
                    }
                  }
                })
              }
            });
          }
          return;
        }

        // Unclear if this is possible - log it as an error if it does
        Raven.captureMessage('confirmPaymentIntent status not succeeded', {
          extra: result.paymentIntent
        });
        client.writeQuery({
          query: GET_CHECKOUT_SESSION,
          data: {
            checkoutSession: Object.assign({}, checkoutSession, {
              status: 'FAILED',
              paymentDetails: {
                paymentMethod: {
                  type: formattedJSON.paymentMethod
                },
                customer: checkoutSession.paymentDetails.customer
              }
            })
          }
        });
        return;
      }
    }).catch(errorMsg => {
      // Only show the generic error msg if there are no other alerts from the useMutation hook above
      if (!Object.keys(FloatingAlertStore.getAlerts()).length) {
        FloatingAlertStore.addAlert({
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.errorAlert.payment.title"
          }),
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.errorAlert.payment.text"
          }),
          type: 'danger'
        });
        Raven.captureMessage(`Payment Element createPaymentIntent was unsuccessful, ${errorMsg}`, {
          extra: errorMsg
        });
      }
    }).finally(() => {
      if (shouldUseAuthModal && args && args.setPaymentAuthorizationModalOpen) {
        const {
          setPaymentAuthorizationModalOpen
        } = args;
        setPaymentAuthorizationModalOpen(false);
      }
      setIsCheckoutSubmitPending(false);
    });
  }, [stripe, elements, resetError, client, formState, setIsCheckoutSubmitPending, isIframeableUrl, createPaymentIntent, iframeableContext, buyerOverridesLineItems, partialPaymentAmount, isRouteIframePreview, dispatch, taxCalculationId]);
  return {
    submit,
    loading: formState.get('isCheckoutSubmitPending') || loading,
    error: error || confirmPaymentErrorMessage || confirmIntentError,
    errorMessage: confirmPaymentErrorMessage,
    isRecaptchaRetry,
    resetError
  };
};